
'use client';

import * as React from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardFooter, CardTitle, CardDescription } from '@/components/ui/card';
import { MessageSquare, ThumbsUp, Info, Trash2, Send, GraduationCap, Loader2 } from 'lucide-react';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Post, Comment } from '@/lib/types';
import { useAuth } from '@/context/auth-context';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './ui/alert-dialog';
import AuthGate from './auth-gate';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from './ui/form';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { cn } from '@/lib/utils';
import { collection, doc, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from '@/lib/firebase';


const CreatePostForm = ({ onAddPost, isLoading }: { onAddPost: (post: { title: string; content: string }) => void, isLoading: boolean }) => {
  const formSchema = z.object({
    title: z.string().min(5, "Title must be at least 5 characters long."),
    content: z.string().min(10, "Content must be at least 10 characters long."),
  });

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", content: "" },
  });

  const onSubmit = (values: z.infer<typeof formSchema>) => {
    onAddPost(values);
    form.reset();
  };

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold">Create a New Post</h2>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Title</FormLabel>
                  <FormControl>
                    <Input placeholder="What's on your mind?" {...field} disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Content</FormLabel>
                  <FormControl>
                    <Textarea placeholder="Share more details here..." {...field} disabled={isLoading} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Post
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

const AddCommentForm = ({ onAddComment, isLoading }: { onAddComment: (text: string) => void, isLoading: boolean }) => {
  const [text, setText] = React.useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (text.trim()) {
      onAddComment(text);
      setText('');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex gap-2 items-center w-full mt-4">
      <Input
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Write a comment..."
        className="flex-1"
        disabled={isLoading}
      />
      <Button type="submit" size="icon" disabled={isLoading}>
        {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="w-4 h-4" />}
      </Button>
    </form>
  );
};


export const PostCard = ({ 
    post, 
    onDelete,
    onLike,
    onAddComment,
    onDeleteComment
}: { 
    post: Post; 
    onDelete: (postId: string) => Promise<void>;
    onLike: (postId: string) => Promise<void>;
    onAddComment: (postId: string, text: string) => Promise<void>;
    onDeleteComment: (postId: string, commentId: string) => Promise<void>;
}) => {
  const { user } = useAuth();
  const [comments, setComments] = React.useState<Comment[]>([]);
  const [loadingState, setLoadingState] = React.useState({ like: false, comment: false, delete: false });
  
  React.useEffect(() => {
    const commentsCol = collection(db, "posts", post.id, "comments");
    const q = query(commentsCol, orderBy("createdAt", "asc"));

    const unsubscribe = onSnapshot(q, (snapshot) => {
        const commentsData: Comment[] = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data(),
            createdAt: doc.data().createdAt?.toDate().toLocaleString() ?? new Date().toLocaleString(),
        } as Comment));
        setComments(commentsData);
    });

    return () => unsubscribe();
  }, [post.id]);


  const canDeletePost = user && (user.uid === post.author.uid || user.isOwner);
  const isLiked = user && post.likedBy && post.likedBy.includes(user.uid);

  const handleLike = async () => {
    setLoadingState(prev => ({ ...prev, like: true }));
    await onLike(post.id);
    setLoadingState(prev => ({ ...prev, like: false }));
  }

  const handleAddComment = async (text: string) => {
    setLoadingState(prev => ({ ...prev, comment: true }));
    await onAddComment(post.id, text);
    setLoadingState(prev => ({ ...prev, comment: false }));
  }

  const handleDeletePost = async () => {
    setLoadingState(prev => ({ ...prev, delete: true }));
    await onDelete(post.id);
    // component will unmount
  }
  
  return (
    <Card className="bg-background">
      <CardHeader>
        <div className="flex items-start gap-3">
          <Avatar>
            <AvatarImage src={post.author.avatar} alt={post.author.name} data-ai-hint={post.author.aiHint} />
            <AvatarFallback>{post.author.name.charAt(0)}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <p className="font-semibold text-foreground">{post.author.name}</p>
              <div className="flex items-center gap-1">
                {post.author.isOwner && <Badge variant="secondary" className="border-primary/50 bg-primary/20 text-primary text-xs">Owner</Badge>}
                {post.author.isEarlySupporter && <Badge variant="secondary" className="border-yellow-500/50 bg-yellow-500/10 text-yellow-600 text-xs">Early Supporter</Badge>}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{post.createdAt}</p>
          </div>
          {canDeletePost && (
             <div className="ml-auto">
               <AlertDialog>
                 <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="text-muted-foreground hover:text-destructive" disabled={loadingState.delete}>
                        {loadingState.delete ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                    </Button>
                 </AlertDialogTrigger>
                 <AlertDialogContent>
                   <AlertDialogHeader>
                     <AlertDialogTitle>Are you sure?</AlertDialogTitle>
                     <AlertDialogDescription>
                       This action cannot be undone. This will permanently delete your post.
                     </AlertDialogDescription>
                   </AlertDialogHeader>
                   <AlertDialogFooter>
                     <AlertDialogCancel>Cancel</AlertDialogCancel>
                     <AlertDialogAction onClick={handleDeletePost} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                   </AlertDialogFooter>
                 </AlertDialogContent>
               </AlertDialog>
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <h3 className="text-lg font-bold text-foreground mb-2">{post.title}</h3>
        <p className="text-sm text-muted-foreground whitespace-pre-wrap">{post.content}</p>
      </CardContent>
      <CardFooter className="flex flex-col items-start gap-4">
        <div className="flex justify-between items-center w-full text-muted-foreground">
            <div className="flex gap-2">
            <Button variant="ghost" size="sm" onClick={handleLike} disabled={loadingState.like}>
                {loadingState.like ? <Loader2 className="w-4 h-4 animate-spin" /> : <ThumbsUp className={cn("w-4 h-4 mr-2", isLiked && "text-primary fill-primary")} />}
                <span>{post.likedBy?.length || 0}</span>
            </Button>
            <div className="flex items-center gap-2 text-sm p-2">
                <MessageSquare className="w-4 h-4" />
                <span>{comments.length}</span>
            </div>
            </div>
        </div>

        {comments.length > 0 && (
          <div className="w-full space-y-3 pt-3">
             <Separator />
            {comments.map((comment) => {
              const canDeleteComment = user && (user.uid === comment.author.uid || user.isOwner);
              return (
              <div key={comment.id} className="flex items-start gap-3 text-sm group">
                <Avatar className="w-8 h-8">
                  <AvatarImage src={comment.author.avatar} alt={comment.author.name} data-ai-hint={comment.author.aiHint} />
                  <AvatarFallback>{comment.author.name.charAt(0)}</AvatarFallback>
                </Avatar>
                <div className="bg-muted p-2 rounded-lg flex-1">
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-foreground text-xs">{comment.author.name}</p>
                     <div className="flex items-center gap-1">
                        {comment.author.isOwner && <Badge variant="secondary" className="border-primary/50 bg-primary/20 text-primary text-xs">Owner</Badge>}
                        {comment.author.isEarlySupporter && <Badge variant="secondary" className="border-yellow-500/50 bg-yellow-500/10 text-yellow-600 text-xs">Early Supporter</Badge>}
                    </div>
                  </div>
                  <p className="text-muted-foreground">{comment.text}</p>
                </div>
                 {canDeleteComment && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive opacity-0 group-hover:opacity-100 transition-opacity">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete this comment?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => onDeleteComment(post.id, comment.id)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
              </div>
            )})}
          </div>
        )}
        <AddCommentForm onAddComment={handleAddComment} isLoading={loadingState.comment} />
      </CardFooter>
    </Card>
  );
};

const StudentAssociations = () => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GraduationCap className="w-6 h-6 text-primary" />
          Student Associations
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
            <h3 className="font-semibold text-lg">Griffith University Muslim Students Association (GUMSA)</h3>
            <p className="text-sm text-muted-foreground">
                The Griffith University Muslim Students Association (GUMSA) is a vibrant and welcoming community for Muslim students across the Gold Coast and Brisbane campuses. We aim to create a space where students can strengthen their faith, build lifelong friendships, and feel supported throughout their university journey.
            </p>
        </div>
        <Separator />
        <div className="space-y-2">
            <h4 className="font-semibold">Programs & Activities</h4>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
                <li>Yearly Ramadan Iftar and annual Eid BBQ.</li>
                <li>Sisters-only and brothers-only gatherings.</li>
                <li>Sports days, tournaments, and collaborations with other MSAs.</li>
                <li>Social events like Family Feud nights.</li>
                <li>Workshops and discussions with guest speakers.</li>
            </ul>
        </div>
      </CardContent>
    </Card>
);

interface CommunityViewProps {
  posts: Post[];
  onAddPost: (post: { title: string; content: string }) => Promise<void>;
  onDeletePost: (postId: string) => Promise<void>;
  onLikePost: (postId: string) => Promise<void>;
  onAddComment: (postId: string, text: string) => Promise<void>;
  onDeleteComment: (postId: string, commentId: string) => Promise<void>;
}

export default function CommunityView({ posts, onAddPost, onDeletePost, onLikePost, onAddComment, onDeleteComment }: CommunityViewProps) {
  const { user } = useAuth();
  const [formLoading, setFormLoading] = React.useState(false);
  
  if (!user) {
    return <AuthGate />;
  }
  
  const handleAddPost = async (post: {title: string; content: string}) => {
    setFormLoading(true);
    await onAddPost(post);
    setFormLoading(false);
  }

  return (
    <div className="flex flex-col h-full bg-background">
      <header className="p-4 border-b sticky top-0 bg-background z-10 flex justify-between items-center">
        <h1 className="text-xl md:text-2xl font-bold text-foreground">Community Hub</h1>
      </header>
      <div className="flex-1 flex flex-col">
        <ScrollArea className="flex-1">
          <div className="p-4 space-y-4">
            <StudentAssociations />
            <Separator />
            <CreatePostForm onAddPost={handleAddPost} isLoading={formLoading} />
            {posts.map((post) => (
              <PostCard 
                key={post.id} 
                post={post}
                onDelete={onDeletePost}
                onLike={onLikePost}
                onAddComment={onAddComment}
                onDeleteComment={onDeleteComment}
              />
            ))}
          </div>
        </ScrollArea>
         <footer className="p-4 border-t bg-background">
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Info className="w-4 h-4" />
            <div>
              <p>The app is still in development, so there might be some bugs and errors.</p>
              <p>Please report if you find any.</p>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}
